import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RecrutamentoSilver = ({ token, user, onLogout }) => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({
    pendente: 0,
    em_progresso: 0,
    pausado: 0,
    ganho: 0,
    perdido: 0
  });
  const [loading, setLoading] = useState(true);
  const tableContainerRef = useRef(null); // ⭐ REF para scroll horizontal
  const [registrosPorPagina, setRegistrosPorPagina] = useState(50);
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [editingCell, setEditingCell] = useState(null); // { leadId, field }
  const [editValue, setEditValue] = useState('');
  const [showNovaOportunidade, setShowNovaOportunidade] = useState(false);
  const [novoLead, setNovoLead] = useState({
    nome: '',
    email: '',
    celular: '',
    estado: '',
    cidade: ''
  });
  const [viewMode, setViewMode] = useState('table'); // 'table', 'grid', 'kanban'
  const [columnFilters, setColumnFilters] = useState({
    nome: '',
    data_contato: '',
    bairro: '',
    data_nascimento: '',
    whatsapp: '',
    data_assinatura: ''
  });
  const [activeFilters, setActiveFilters] = useState({
    nome: '',
    data_contato: '',
    bairro: '',
    data_nascimento: '',
    whatsapp: '',
    data_assinatura: ''
  });
  const [showFilterInput, setShowFilterInput] = useState({
    nome: false,
    data_contato: false,
    bairro: false,
    data_nascimento: false,
    whatsapp: false,
    data_assinatura: false
  });

  useEffect(() => {
    fetchLeads();
    fetchStats();
    window.openNovaOportunidade = () => setShowNovaOportunidade(true);
    
    // ⭐ SCROLL HORIZONTAL COM MOUSE WHEEL
    const handleWheelScroll = (e) => {
      if (tableContainerRef.current && tableContainerRef.current.contains(e.target)) {
        if (tableContainerRef.current.scrollWidth > tableContainerRef.current.clientWidth) {
          e.preventDefault();
          tableContainerRef.current.scrollLeft += e.deltaY;
        }
      }
    };

    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheelScroll, { passive: false });
    }
    
    return () => {
      delete window.openNovaOportunidade;
      if (container) {
        container.removeEventListener('wheel', handleWheelScroll);
      }
    };
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads-recrutamento`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (error) {
      console.error('Erro ao buscar leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads-recrutamento/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads-recrutamento/export/excel`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `oportunidades_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        alert('Relatório exportado com sucesso!');
      } else {
        alert('Erro ao exportar relatório');
      }
    } catch (error) {
      console.error('Erro ao exportar:', error);
      alert('Erro ao exportar relatório');
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      alert('Apenas arquivos Excel (.xlsx, .xls) são permitidos');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/leads-recrutamento/import/excel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Importação concluída! ${data.added} adicionados, ${data.updated} atualizados`);
        fetchLeads();
        fetchStats();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erro ao importar arquivo');
      }
    } catch (error) {
      console.error('Erro ao importar:', error);
      alert('Erro ao importar arquivo');
    }

    event.target.value = '';
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads-recrutamento/${leadId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        fetchLeads();
        fetchStats();
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  const toggleFavorito = async (leadId, currentFavorito) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads-recrutamento/${leadId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ favorito: !currentFavorito })
      });
      if (response.ok) {
        fetchLeads();
      }
    } catch (error) {
      console.error('Erro ao atualizar favorito:', error);
    }
  };

  const handleCellEdit = (leadId, field, currentValue) => {
    setEditingCell({ leadId, field });
    setEditValue(currentValue || '');
  };

  const handleSaveEdit = async (leadId, field, value = null) => {
    const valueToSave = value !== null ? value : editValue;
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads-recrutamento/${leadId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: valueToSave })
      });
      if (response.ok) {
        fetchLeads();
        setEditingCell(null);
        setEditValue('');
      }
    } catch (error) {
      console.error('Erro ao salvar edição:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleCriarOportunidade = async () => {
    if (!novoLead.nome || !novoLead.celular) {
      alert('Nome e Celular são obrigatórios!');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/leads-recrutamento`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nome: novoLead.nome,
          whatsapp: novoLead.celular,
          primeiro_contato_rh: '',
          data_contato: new Date().toLocaleDateString('pt-BR'),
          bairro: novoLead.cidade || '',
          data_nascimento: '',
          possui_creci: '',
          estavel_financeiramente: '',
          segunda_etapa_broker: '',
          assinatura_associacao: '',
          data_assinatura: '',
          informacoes_adicionais: `Email: ${novoLead.email || '-'} | Estado: ${novoLead.estado || '-'} | Cidade: ${novoLead.cidade || '-'}`,
          origem_lead: ''
        })
      });

      if (response.ok) {
        fetchLeads();
        fetchStats();
        setShowNovaOportunidade(false);
        setNovoLead({ nome: '', email: '', celular: '', estado: '', cidade: '' });
      }
    } catch (error) {
      console.error('Erro ao criar oportunidade:', error);
      alert('Erro ao criar oportunidade!');
    }
  };

  // ⭐ NOVA FUNÇÃO: Excluir lead
  const handleDeleteLead = async (leadId, leadNome) => {
    if (!window.confirm(`Tem certeza que deseja excluir o lead ${leadNome}? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/leads-recrutamento/${leadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Erro ao excluir lead');

      alert('Lead excluído com sucesso!');
      fetchLeads(); // Recarrega a lista
      fetchStats(); // Atualiza estatísticas
    } catch (error) {
      console.error('Erro ao excluir lead:', error);
      alert('Erro ao excluir lead');
    }
  };


  const renderFilterableHeader = (label, column) => {
    const hasActiveFilter = activeFilters[column];
    
    return (
      <th 
        style={{ 
          padding: '12px', 
          textAlign: 'left', 
          fontWeight: '600', 
          fontSize: '12px',
          position: 'relative'
        }}
        onMouseEnter={(e) => {
          const icon = e.currentTarget.querySelector('.search-icon');
          if (icon) icon.style.opacity = '1';
        }}
        onMouseLeave={(e) => {
          const icon = e.currentTarget.querySelector('.search-icon');
          if (icon && !showFilterInput[column]) icon.style.opacity = '0';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
          <span>{label}</span>
          <button
            className="search-icon"
            onClick={() => toggleFilterInput(column)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              opacity: showFilterInput[column] || hasActiveFilter ? '1' : '0',
              transition: 'opacity 0.2s',
              color: hasActiveFilter ? '#10B981' : 'white'
            }}
            title="Pesquisar"
          >
            🔍
          </button>
        </div>
        
        {showFilterInput[column] && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '0',
            zIndex: 1000,
            backgroundColor: 'white',
            border: '2px solid #3B82F6',
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: '250px',
            marginTop: '4px'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <input
                type="text"
                value={columnFilters[column]}
                onChange={(e) => setColumnFilters(prev => ({
                  ...prev,
                  [column]: e.target.value
                }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleApplyFilter(column);
                  } else if (e.key === 'Escape') {
                    setShowFilterInput(prev => ({ ...prev, [column]: false }));
                  }
                }}
                placeholder={`Pesquisar ${label.toLowerCase()}...`}
                autoFocus
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '13px',
                  width: '100%',
                  color: '#1F2937'
                }}
              />
              <button
                onClick={() => handleApplyFilter(column)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✓
              </button>
            </div>
          </div>
        )}
      </th>
    );
  };

  const renderEditableCell = (lead, field, value, options = null) => {
    const isEditing = editingCell?.leadId === lead.id && editingCell?.field === field;
    
    if (isEditing) {
      return (
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', minWidth: '250px' }}>
          {options ? (
            <>
              <select
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveEdit(lead.id, field);
                  } else if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
                autoFocus
                style={{
                  padding: '10px 12px',
                  border: '2px solid #3B82F6',
                  borderRadius: '6px',
                  fontSize: '14px',
                  width: '100%',
                  minWidth: '200px',
                  backgroundColor: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  cursor: 'pointer'
                }}
              >
                <option value="">Selecione...</option>
                {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              <button
                onClick={() => handleSaveEdit(lead.id, field)}
                style={{
                  padding: '10px 14px',
                  background: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '700',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  minWidth: '40px'
                }}
                title="Salvar (Enter)"
              >
                ✓
              </button>
            </>
          ) : (
            <>
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveEdit(lead.id, field);
                  } else if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
                autoFocus
                style={{
                  padding: '10px 12px',
                  border: '2px solid #3B82F6',
                  borderRadius: '6px',
                  fontSize: '14px',
                  width: '100%',
                  minWidth: '200px',
                  backgroundColor: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              />
              <button
                onClick={() => handleSaveEdit(lead.id, field)}
                style={{
                  padding: '10px 14px',
                  background: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  minWidth: '40px'
                }}
                title="Salvar (Enter)"
              >
                ✓
              </button>
              <button
                onClick={handleCancelEdit}
                style={{
                  padding: '10px 14px',
                  background: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  minWidth: '40px'
                }}
                title="Cancelar (Esc)"
              >
                ✕
              </button>
            </>
          )}
        </div>
      );
    }
    
    return (
      <div
        onClick={() => handleCellEdit(lead.id, field, value)}
        style={{
          cursor: 'pointer',
          padding: '6px',
          borderRadius: '4px',
          transition: 'background 0.2s',
          minHeight: '24px'
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        title="Clique para editar"
      >
        {value || '-'}
      </div>
    );
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Pendente': '⚠️',
      'Em progresso': '💼',
      'Pausado': '⏸️',
      'Ganho': '👍',
      'Perdido': '👎'
    };
    return icons[status] || '❓';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pendente': '#F59E0B',
      'Em progresso': '#3B82F6',
      'Pausado': '#6B7280',
      'Ganho': '#10B981',
      'Perdido': '#EF4444'
    };
    return colors[status] || '#6B7280';
  };

  const getProgressDots = (status) => {
    const stages = ['Pendente', 'Em progresso', 'Ganho'];
    const currentStage = stages.indexOf(status);
    
    return (
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center' }}>
        {stages.map((stage, index) => {
          let color = '#E5E7EB';
          if (status === 'Perdido') {
            color = '#EF4444';
          } else if (status === 'Pausado') {
            color = '#F59E0B';
          } else if (index <= currentStage) {
            if (stage === 'Ganho') color = '#10B981';
            else if (stage === 'Em progresso') color = '#F59E0B';
            else color = '#EF4444';
          }
          
          return (
            <div
              key={stage}
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: color,
                border: '1px solid rgba(0,0,0,0.1)'
              }}
              title={stage}
            />
          );
        })}
      </div>
    );
  };

  const handleApplyFilter = (column) => {
    setActiveFilters(prev => ({
      ...prev,
      [column]: columnFilters[column]
    }));
  };

  const handleClearFilter = (column) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: ''
    }));
    setActiveFilters(prev => ({
      ...prev,
      [column]: ''
    }));
    setShowFilterInput(prev => ({
      ...prev,
      [column]: false
    }));
  };

  const toggleFilterInput = (column) => {
    setShowFilterInput(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const filteredAndSortedLeads = leads.filter(lead => {
    const matchesStatus = !statusFilter || lead.status === statusFilter;
    
    // Apply column filters
    const matchesNome = !activeFilters.nome || 
      (lead.nome && lead.nome.toLowerCase().includes(activeFilters.nome.toLowerCase()));
    
    const matchesDataContato = !activeFilters.data_contato || 
      (lead.data_contato && lead.data_contato.toLowerCase().includes(activeFilters.data_contato.toLowerCase()));
    
    const matchesBairro = !activeFilters.bairro || 
      (lead.bairro && lead.bairro.toLowerCase().includes(activeFilters.bairro.toLowerCase()));
    
    const matchesDataNascimento = !activeFilters.data_nascimento || 
      (lead.data_nascimento && lead.data_nascimento.toLowerCase().includes(activeFilters.data_nascimento.toLowerCase()));
    
    const matchesWhatsapp = !activeFilters.whatsapp || 
      (lead.whatsapp && lead.whatsapp.toLowerCase().includes(activeFilters.whatsapp.toLowerCase()));
    
    const matchesDataAssinatura = !activeFilters.data_assinatura || 
      (lead.data_assinatura && lead.data_assinatura.toLowerCase().includes(activeFilters.data_assinatura.toLowerCase()));
    
    return matchesStatus && matchesNome && matchesDataContato && matchesBairro && 
           matchesDataNascimento && matchesWhatsapp && matchesDataAssinatura;
  }).sort((a, b) => {
    if (!sortBy) return 0;
    
    switch (sortBy) {
      case 'nome_asc':
        return (a.nome || a.candidato).localeCompare(b.nome || b.candidato);
      case 'nome_desc':
        return (b.nome || b.candidato).localeCompare(a.nome || a.candidato);
      case 'data_asc':
        return new Date(a.data_contato || 0) - new Date(b.data_contato || 0);
      case 'data_desc':
        return new Date(b.data_contato || 0) - new Date(a.data_contato || 0);
      case 'status_asc':
        return a.status.localeCompare(b.status);
      case 'status_desc':
        return b.status.localeCompare(a.status);
      default:
        return 0;
    }
  });

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Carregando...</div>;
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#F3F4F6' }}>
      <Sidebar currentPage="recrutamento" user={user} onLogout={onLogout} />

      {/* Main Content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {/* Top Header */}
        <header style={{ backgroundColor: 'white', borderBottom: '1px solid #E5E7EB', padding: '15px 30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1F2937', margin: 0 }}>OPORTUNIDADES</h1>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{ fontSize: '12px', color: '#6B7280' }}>
                {user.role === 'administrador' ? 'Administrador' : 'Gestor'}
              </div>
              <div style={{
                width: '35px',
                height: '35px',
                borderRadius: '50%',
                backgroundColor: '#2563EB',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                {user.nome.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        </header>

      <div style={{ padding: '0 20px 20px' }}>
        {/* Page Title */}
        <div style={{ marginBottom: '15px' }}>
          <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: '#1F2937', marginBottom: '5px', margin: 0 }}>
            OPORTUNIDADES
          </h2>
          <div style={{ color: '#6B7280', fontSize: '12px', marginTop: '3px' }}>
            <span>Dashboard</span> <span style={{ margin: '0 5px' }}>›</span> <span>Oportunidades</span>
          </div>
        </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '15px' }}>
        <Card style={{ background: '#F59E0B', color: 'white', padding: '20px 15px', borderRadius: '6px', textAlign: 'center' }}>
          <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{stats.pendente}</div>
          <div style={{ fontSize: '14px', marginTop: '8px' }}>Pendente</div>
        </Card>

        <Card style={{ background: '#3B82F6', color: 'white', padding: '20px 15px', borderRadius: '6px', textAlign: 'center' }}>
          <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{stats.em_progresso}</div>
          <div style={{ fontSize: '14px', marginTop: '8px' }}>Em progresso</div>
        </Card>

        <Card style={{ background: '#1F2937', color: 'white', padding: '20px 15px', borderRadius: '6px', textAlign: 'center' }}>
          <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{stats.pausado}</div>
          <div style={{ fontSize: '14px', marginTop: '8px' }}>Pausado</div>
        </Card>

        <Card style={{ background: '#10B981', color: 'white', padding: '20px 15px', borderRadius: '6px', textAlign: 'center' }}>
          <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{stats.ganho}</div>
          <div style={{ fontSize: '14px', marginTop: '8px' }}>Ganho</div>
        </Card>

        <Card style={{ background: '#EF4444', color: 'white', padding: '20px 15px', borderRadius: '6px', textAlign: 'center' }}>
          <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{stats.perdido}</div>
          <div style={{ fontSize: '14px', marginTop: '8px' }}>Perdido</div>
        </Card>
      </div>

      {/* Toolbar */}
      <Card style={{ padding: '12px', marginBottom: '15px', backgroundColor: 'white', borderRadius: '6px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px', color: '#6B7280' }}
            >
              <option value="">Filtros rápidos</option>
              <option value="Pendente">Pendente</option>
              <option value="Em progresso">Em progresso</option>
              <option value="Pausado">Pausado</option>
              <option value="Ganho">Ganho</option>
              <option value="Perdido">Perdido</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px', color: '#6B7280' }}
            >
              <option value="">Ordenar registros</option>
              <option value="nome_asc">Nome (A-Z)</option>
              <option value="nome_desc">Nome (Z-A)</option>
              <option value="data_asc">Data (Mais antiga)</option>
              <option value="data_desc">Data (Mais recente)</option>
              <option value="status_asc">Status (A-Z)</option>
              <option value="status_desc">Status (Z-A)</option>
            </select>
            <button
              onClick={handleExportExcel}
              style={{
                padding: '6px 12px',
                backgroundColor: '#10B981',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              📊 Exportar Relatório
            </button>
            <label>
              <button
                type="button"
                onClick={() => document.getElementById('import-leads-excel').click()}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                📥 Importar Relatório
              </button>
              <input
                id="import-leads-excel"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportExcel}
                style={{ display: 'none' }}
              />
            </label>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: '#6B7280', marginRight: '5px' }}>Registros por página</span>
            <select 
              value={registrosPorPagina}
              onChange={(e) => setRegistrosPorPagina(Number(e.target.value))}
              style={{ padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '13px' }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card style={{ backgroundColor: 'white', borderRadius: '6px', overflow: 'hidden' }}>
        <div 
          ref={tableContainerRef}
          style={{ 
            overflowX: 'auto',
            cursor: 'grab',
            scrollbarWidth: 'thin',
            scrollbarColor: '#CBD5E0 #F7FAFC'
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead style={{ backgroundColor: '#9CA3AF', color: 'white' }}>
              <tr>
                {renderFilterableHeader('NOME', 'nome')}
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '12px' }}>1° CONTATO RH</th>
                {renderFilterableHeader('DATA DE CONTATO', 'data_contato')}
                {renderFilterableHeader('BAIRRO', 'bairro')}
                {renderFilterableHeader('DATA DE NASCIMENTO', 'data_nascimento')}
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '12px' }}>POSSUI CRECI?</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '12px' }}>ESTÁVEL FINANCEIRAMENTE?</th>
                {renderFilterableHeader('WHATSAPP', 'whatsapp')}
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '12px' }}>2° ETAPA BROKER</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '12px' }}>ASSINATURA DE ASSOCIAÇÃO</th>
                {renderFilterableHeader('DATA DE ASSINATURA', 'data_assinatura')}
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '12px' }}>ORIGEM DO LEAD</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '12px' }}>INFORMAÇÕES ADICIONAIS</th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>AÇÕES</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedLeads.slice(0, registrosPorPagina).map((lead, index) => (
                <tr key={lead.id} style={{ borderBottom: '1px solid #E5E7EB', backgroundColor: index % 2 === 0 ? 'white' : '#F0FDF4' }}>
                  <td style={{ padding: '12px', fontWeight: '600', color: '#1F2937', fontSize: '13px' }}>
                    {renderEditableCell(lead, 'nome', lead.nome)}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '12px' }}>
                    {renderEditableCell(lead, 'primeiro_contato_rh', lead.primeiro_contato_rh, ['SIM', 'NÃO', 'AGUARDO', 'NÃO RESPONDEU', 'REPROVADO'])}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '12px' }}>
                    {renderEditableCell(lead, 'data_contato', lead.data_contato)}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '12px' }}>
                    {renderEditableCell(lead, 'bairro', lead.bairro)}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '12px' }}>
                    {renderEditableCell(lead, 'data_nascimento', lead.data_nascimento)}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '12px' }}>
                    {renderEditableCell(lead, 'possui_creci', lead.possui_creci)}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '12px' }}>
                    {renderEditableCell(lead, 'estavel_financeiramente', lead.estavel_financeiramente)}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '12px' }}>
                    {renderEditableCell(lead, 'whatsapp', lead.whatsapp)}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '12px' }}>
                    {renderEditableCell(lead, 'segunda_etapa_broker', lead.segunda_etapa_broker, ['SIM', 'NÃO', 'AGUARDO', 'NÃO RESPONDEU', 'REPROVADO'])}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '12px' }}>
                    {renderEditableCell(lead, 'assinatura_associacao', lead.assinatura_associacao, ['SIM', 'NÃO', 'AGUARDO'])}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '12px' }}>
                    {renderEditableCell(lead, 'data_assinatura', lead.data_assinatura)}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '12px' }}>
                    {renderEditableCell(lead, 'origem_lead', lead.origem_lead)}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280', fontSize: '12px', maxWidth: '200px' }}>
                    {renderEditableCell(lead, 'informacoes_adicionais', lead.informacoes_adicionais)}
                  </td>
                  {/* ⭐ NOVA COLUNA DE AÇÕES COM BOTÃO EXCLUIR */}
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleDeleteLead(lead.id, lead.nome)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '8px',
                        borderRadius: '6px',
                        transition: 'all 0.2s',
                        color: '#EF4444',
                        fontSize: '18px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#FEE2E2';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                      title="Excluir lead"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAndSortedLeads.length === 0 && (
          <div style={{ padding: '30px', textAlign: 'center', color: '#6B7280', fontSize: '13px' }}>
            Nenhuma oportunidade encontrada
          </div>
        )}
      </Card>
      </div>
      </div>

      {/* Modal Nova Oportunidade */}
      {showNovaOportunidade && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px', color: '#1F2937' }}>
              ✨ Nova Oportunidade
            </h2>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '24px' }}>
              Cadastre um novo lead no sistema
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Nome do Lead <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={novoLead.nome}
                  onChange={(e) => setNovoLead({ ...novoLead, nome: e.target.value })}
                  placeholder="Digite o nome completo"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3B82F6'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  E-mail do Lead
                </label>
                <input
                  type="email"
                  value={novoLead.email}
                  onChange={(e) => setNovoLead({ ...novoLead, email: e.target.value })}
                  placeholder="exemplo@email.com"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3B82F6'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Celular <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="tel"
                  value={novoLead.celular}
                  onChange={(e) => setNovoLead({ ...novoLead, celular: e.target.value })}
                  placeholder="(00) 00000-0000"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3B82F6'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    Estado
                  </label>
                  <input
                    type="text"
                    value={novoLead.estado}
                    onChange={(e) => setNovoLead({ ...novoLead, estado: e.target.value })}
                    placeholder="Ex: Bahia"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3B82F6'}
                    onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                    Cidade
                  </label>
                  <input
                    type="text"
                    value={novoLead.cidade}
                    onChange={(e) => setNovoLead({ ...novoLead, cidade: e.target.value })}
                    placeholder="Ex: Salvador"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3B82F6'}
                    onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowNovaOportunidade(false);
                  setNovoLead({ nome: '', email: '', celular: '', estado: '', cidade: '' });
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '2px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  background: 'white',
                  color: '#6B7280'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleCriarOportunidade}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  color: 'white',
                  boxShadow: '0 4px 6px rgba(220, 38, 38, 0.3)'
                }}
              >
                Criar Oportunidade
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecrutamentoSilver;
